import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, BookOpen, Lock, Star, Headphones, Download, FileText, Play, Pause, SkipBack, SkipForward, MessageCircle, Send, Trash2, Loader } from "lucide-react";
import { getSeriesBySlug, getChaptersForSeries, getChapter } from "../data/series";
import { useAuth } from "../AuthContext";
import SubstackExportButton from "../components/SubstackExportButton";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChapterPage = () => {
  const { seriesSlug, chapterSlug } = useParams();
  const { user, isPatron, token } = useAuth();
  const [chapterContent, setChapterContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);

  // Audio state
  const [audioStatus, setAudioStatus] = useState("checking"); // checking | none | generating | ready
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [savedPosition, setSavedPosition] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const audioRef = useRef(null);
  const saveTimerRef = useRef(null);
  const pollRef = useRef(null);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  const series = getSeriesBySlug(seriesSlug);
  const chapters = getChaptersForSeries(seriesSlug);
  const chapterNumber = parseInt(chapterSlug?.replace("chapter-", "") || "1");
  const currentChapter = getChapter(seriesSlug, chapterNumber);
  const prevChapter = chapterNumber > 1 ? chapters[chapterNumber - 2] : null;
  const nextChapter = chapterNumber < chapters.length ? chapters[chapterNumber] : null;
  const isPatronSeries = series?.tier === "patron";

  // Fetch chapter content
  useEffect(() => {
    const fetchChapter = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/chapters/${seriesSlug}/${chapterNumber}`);
        setChapterContent(response.data);
      } catch (error) {
        setChapterContent(null);
      } finally {
        setIsLoading(false);
      }
    };
    if (seriesSlug && chapterNumber) fetchChapter();
  }, [seriesSlug, chapterNumber]);

  // Check audio status on load
  useEffect(() => {
    if (!user) { setAudioStatus("none"); return; }
    const check = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/audio/chapter-status/${seriesSlug}/${chapterNumber}`);
        setAudioStatus(res.data.status); // ready | generating | none
        if (res.data.status === "generating") startPolling();
      } catch {
        setAudioStatus("none");
      }
    };
    setAudioStatus("checking");
    check();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [seriesSlug, chapterNumber, user]);

  // Poll for audio ready
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/audio/chapter-status/${seriesSlug}/${chapterNumber}`);
        if (res.data.status === "ready") {
          setAudioStatus("ready");
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* continue polling */ }
    }, 5000);
  }, [seriesSlug, chapterNumber]);

  // Request audio generation
  const handleRequestAudio = async () => {
    if (!user || !token) return;
    setAudioStatus("generating");
    setAutoPlay(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/audio/request/${seriesSlug}/${chapterNumber}?token=${token}`
      );
      startPolling();
    } catch {
      setAudioStatus("none");
    }
  };

  // Auto-play when audio becomes ready
  useEffect(() => {
    if (audioStatus === "ready" && autoPlay && audioRef.current) {
      const tryPlay = () => {
        audioRef.current?.play().catch(() => {});
        setAutoPlay(false);
      };
      // Small delay to let the audio element load
      setTimeout(tryPlay, 500);
    }
  }, [audioStatus, autoPlay]);

  // Load saved playback position
  useEffect(() => {
    if (!user || audioStatus !== "ready") return;
    const loadPosition = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/playback/${seriesSlug}/${chapterNumber}`);
        if (res.data.position > 0) setSavedPosition(res.data.position);
      } catch { /* ignore */ }
    };
    loadPosition();
  }, [seriesSlug, chapterNumber, user, audioStatus]);

  // Save playback position periodically
  const savePosition = useCallback(async (pos) => {
    if (!user || !pos || pos < 1) return;
    try {
      await axios.post(`${BACKEND_URL}/api/playback/save`, {
        series_slug: seriesSlug,
        chapter_number: chapterNumber,
        position: pos,
      });
    } catch { /* ignore */ }
  }, [seriesSlug, chapterNumber, user]);

  // Reading scroll progress
  useEffect(() => {
    let saveTimeout = null;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setReadingProgress(Math.min(progress, 100));
      if (user && saveTimeout) clearTimeout(saveTimeout);
      if (user) {
        saveTimeout = setTimeout(() => {
          axios.post(`${BACKEND_URL}/api/reading-progress/save`, {
            series_slug: seriesSlug, chapter_number: chapterNumber,
            scroll_pct: Math.min(progress, 100),
          }).catch(() => {});
        }, 3000);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => { window.removeEventListener("scroll", handleScroll); if (saveTimeout) clearTimeout(saveTimeout); };
  }, [user, seriesSlug, chapterNumber]);

  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/comments/${seriesSlug}/${chapterNumber}`);
        setComments(res.data);
      } catch { /* ignore */ }
    };
    if (seriesSlug && chapterNumber) fetchComments();
  }, [seriesSlug, chapterNumber]);

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || commentLoading) return;
    setCommentLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/comments`, {
        series_slug: seriesSlug, chapter_number: chapterNumber, text: commentText.trim(),
      });
      setComments([res.data, ...comments]);
      setCommentText("");
    } catch { /* ignore */ }
    setCommentLoading(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    return () => { if (saveTimerRef.current) clearInterval(saveTimerRef.current); };
  }, []);

  // Media Session API
  useEffect(() => {
    if (audioStatus !== "ready" || !audioRef.current || !("mediaSession" in navigator)) return;
    const seriesTitle = series?.title || seriesSlug;
    const chTitle = currentChapter?.title || `Chapter ${chapterNumber}`;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: chTitle, artist: "Freddy Fiction", album: seriesTitle,
    });
    navigator.mediaSession.setActionHandler("play", () => { audioRef.current?.play(); setIsPlaying(true); });
    navigator.mediaSession.setActionHandler("pause", () => { audioRef.current?.pause(); setIsPlaying(false); });
    navigator.mediaSession.setActionHandler("seekbackward", () => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15); });
    navigator.mediaSession.setActionHandler("seekforward", () => { if (audioRef.current) audioRef.current.currentTime = Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + 15); });
    navigator.mediaSession.setActionHandler("seekto", (d) => { if (audioRef.current && d.seekTime != null) audioRef.current.currentTime = d.seekTime; });
  }, [audioStatus, series, seriesSlug, currentChapter, chapterNumber]);

  // Audio controls
  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); savePosition(audioRef.current.currentTime); }
    else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const skip = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + seconds));
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const ct = audioRef.current.currentTime;
    setAudioCurrentTime(ct);
    setAudioProgress(audioRef.current.duration ? (ct / audioRef.current.duration) * 100 : 0);
    if ("mediaSession" in navigator && audioRef.current.duration) {
      navigator.mediaSession.setPositionState({ duration: audioRef.current.duration, playbackRate: audioRef.current.playbackRate, position: ct });
    }
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setAudioDuration(audioRef.current.duration);
    if (savedPosition > 0 && savedPosition < audioRef.current.duration - 5) {
      audioRef.current.currentTime = savedPosition;
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    saveTimerRef.current = setInterval(() => {
      if (audioRef.current) savePosition(audioRef.current.currentTime);
    }, 5000);
  };

  const handlePause = () => {
    setIsPlaying(false);
    if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    if (audioRef.current) savePosition(audioRef.current.currentTime);
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (!series) return <Navigate to="/" replace />;

  // PAYWALL: patron series without patron access
  if (isPatronSeries && !isPatron) {
    const fullText = chapterContent?.content || "";
    const words = fullText.split(/\s+/);
    const previewWords = words.slice(0, 200).join(" ");
    const previewParagraphs = previewWords.split(/\n\n|\n/).filter((p) => p.trim());

    return (
      <div data-testid="chapter-paywall" className="min-h-screen bg-ff-bg">
        <header className="pt-24 md:pt-32 pb-8 md:pb-12">
          <div className="max-w-reading mx-auto px-4 md:px-6">
            <Link to={`/${seriesSlug}`} className="inline-flex items-center gap-2 text-sm text-ff-text-muted hover:text-ff-text transition-colors mb-6">
              <BookOpen size={14} style={{ color: series.accentColor }} /> {series.title}
            </Link>
            <div className="flex items-center gap-3 mb-3">
              <p className="label" style={{ color: series.accentColor }}>CHAPTER {chapterNumber}</p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: "#c8860a22", color: "#c8860a", border: "1px solid #c8860a44" }}>
                <Star size={10} fill="currentColor" /> Patron
              </span>
            </div>
            <h1 data-testid="chapter-title" className="font-serif text-3xl md:text-4xl text-ff-text">{currentChapter?.title}</h1>
          </div>
        </header>

        <article className="pb-0">
          <div className="max-w-reading mx-auto px-4 md:px-6">
            <div className="relative">
              <div data-testid="chapter-preview" className="prose-reading py-8">
                {isLoading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-ff-surface rounded w-3/4" />
                    <div className="h-4 bg-ff-surface rounded" />
                    <div className="h-4 bg-ff-surface rounded w-5/6" />
                  </div>
                ) : (
                  previewParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent 0%, #090E33 85%)" }} />
            </div>

            <div data-testid="paywall-cta" className="relative -mt-8 pb-24">
              <div className="p-10 border text-center" style={{ borderColor: `${series.accentColor}33`, background: `${series.accentColor}06` }}>
                <div className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ background: `${series.accentColor}20` }}>
                  <Lock size={24} style={{ color: series.accentColor }} />
                </div>
                <h2 className="font-serif text-2xl text-ff-text mb-2">Continue reading</h2>
                <p className="text-ff-text-muted text-sm mb-6 max-w-sm mx-auto">
                  {series.title} is available exclusively to Patrons. Unlock all {series.chapterCount} chapters — read or listen — for $9.99/month.
                </p>
                {user ? (
                  <Link to="/subscribe" data-testid="paywall-subscribe-btn" className="btn-primary inline-flex items-center gap-2" style={{ backgroundColor: series.accentColor }}>
                    <Star size={16} /> Upgrade to Patron — $9.99/mo
                  </Link>
                ) : (
                  <div className="space-y-3">
                    <Link to="/login" state={{ from: `/${seriesSlug}/${chapterSlug}` }} data-testid="paywall-login-btn" className="btn-primary inline-flex items-center gap-2" style={{ backgroundColor: series.accentColor }}>
                      Sign In
                    </Link>
                    <p className="text-ff-text-muted text-xs">
                      or <Link to="/signup" state={{ from: `/${seriesSlug}/${chapterSlug}` }} className="text-ff-accent hover:underline">create an account</Link> then subscribe
                    </p>
                  </div>
                )}
                <div className="mt-4">
                  <Link to={`/${seriesSlug}`} className="text-ff-text-muted text-sm hover:text-ff-text transition-colors">&larr; Back to {series.title}</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    );
  }

  if (!currentChapter) return <Navigate to={`/${seriesSlug}`} replace />;

  const ChapterNavigation = ({ position }) => (
    <div data-testid={`chapter-nav-${position}`} className="flex items-center justify-between py-8 border-t border-ff-border">
      {prevChapter ? (
        <Link to={`/${seriesSlug}/${prevChapter.slug}`} data-testid="prev-chapter-btn" className="flex items-center gap-2 text-ff-text-muted hover:text-ff-text transition-colors group">
          <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <div className="text-left">
            <p className="text-xs text-ff-text-muted">Previous</p>
            <p className="text-sm font-serif">{prevChapter.title}</p>
          </div>
        </Link>
      ) : <div />}
      {nextChapter ? (
        <Link to={`/${seriesSlug}/${nextChapter.slug}`} data-testid="next-chapter-btn" className="flex items-center gap-2 text-ff-text-muted hover:text-ff-text transition-colors group">
          <div className="text-right">
            <p className="text-xs text-ff-text-muted">Next</p>
            <p className="text-sm font-serif">{nextChapter.title}</p>
          </div>
          <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      ) : (
        <Link to={`/${seriesSlug}`} data-testid="back-to-series-btn" className="flex items-center gap-2 text-ff-text-muted hover:text-ff-text transition-colors">
          <span className="text-sm">Back to {series.title}</span>
          <ChevronRight size={16} />
        </Link>
      )}
    </div>
  );

  // Audio section: always visible for logged-in users
  const AudioSection = () => {
    if (!user) {
      return (
        <div data-testid="audio-login-prompt" className="max-w-reading mx-auto px-4 md:px-6 mb-8">
          <div className="p-5 border border-ff-border bg-ff-surface/50 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: `${series.accentColor}15` }}>
              <Headphones size={20} style={{ color: series.accentColor }} />
            </div>
            <div className="flex-1">
              <p className="text-ff-text text-sm font-medium">Listen to this chapter</p>
              <p className="text-ff-text-muted text-xs mt-0.5">
                <Link to="/login" state={{ from: `/${seriesSlug}/${chapterSlug}` }} className="underline hover:text-ff-text transition-colors" style={{ color: series.accentColor }}>Sign in</Link> to unlock the audio version.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Generating state
    if (audioStatus === "generating" || audioStatus === "checking") {
      return (
        <div data-testid="audio-generating" className="max-w-reading mx-auto px-4 md:px-6 mb-8">
          <div className="p-5 border border-ff-border bg-ff-surface">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 animate-pulse" style={{ background: `${series.accentColor}20` }}>
                {audioStatus === "checking" ? (
                  <Headphones size={20} style={{ color: series.accentColor }} />
                ) : (
                  <Loader size={20} className="animate-spin" style={{ color: series.accentColor }} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-ff-text text-sm font-medium flex items-center gap-2">
                  {audioStatus === "checking" ? "Checking audio..." : "Preparing your audio..."}
                </p>
                <p className="text-ff-text-muted text-xs mt-0.5">
                  {audioStatus === "generating" ? "Your chapter is being narrated. This usually takes about a minute." : "One moment..."}
                </p>
              </div>
            </div>
            {audioStatus === "generating" && (
              <div className="mt-4 h-1 bg-ff-border rounded-full overflow-hidden">
                <div className="h-full rounded-full animate-pulse" style={{ width: "60%", background: series.accentColor, animation: "pulse 2s ease-in-out infinite" }} />
              </div>
            )}
          </div>
        </div>
      );
    }

    // Not yet generated — show CTA
    if (audioStatus === "none") {
      return (
        <div data-testid="audio-request-cta" className="max-w-reading mx-auto px-4 md:px-6 mb-8">
          <div className="p-5 border border-ff-border bg-ff-surface/50 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: `${series.accentColor}15` }}>
              <Headphones size={20} style={{ color: series.accentColor }} />
            </div>
            <div className="flex-1">
              <p className="text-ff-text text-sm font-medium">Listen to this chapter</p>
              <p className="text-ff-text-muted text-xs mt-0.5">A narrated audio version, ready in about a minute.</p>
            </div>
            <button
              onClick={handleRequestAudio}
              data-testid="generate-audio-btn"
              className="px-5 py-2.5 text-sm font-medium transition-all hover:brightness-110 shrink-0 flex items-center gap-2"
              style={{ background: series.accentColor, color: "#090E33" }}
            >
              <Play size={14} /> Listen
            </button>
          </div>
        </div>
      );
    }

    // Ready — show full player
    return (
      <div data-testid="audio-player-bar" className="max-w-reading mx-auto px-4 md:px-6 mb-8">
        <div className="p-4 bg-ff-surface border border-ff-border">
          <div className="flex items-center justify-between mb-3">
            <span className="label flex items-center gap-1.5" style={{ color: series.accentColor }}>
              <Headphones size={12} /> LISTENING
              {savedPosition > 0 && !isPlaying && audioCurrentTime < 1 && (
                <span className="text-ff-text-muted text-[10px] ml-2">Resume at {formatTime(savedPosition)}</span>
              )}
            </span>
            <div className="flex items-center gap-3">
              <a href={`${BACKEND_URL}/api/audio/download/${seriesSlug}/${chapterNumber}?token=${token}`} data-testid="download-mp3" className="inline-flex items-center gap-1.5 text-xs text-ff-text-muted hover:text-ff-text transition-colors">
                <Download size={14} /> Download MP3
              </a>
              {isPatron && (
                <>
                  <a href={`${BACKEND_URL}/api/download/pdf/chapter/${seriesSlug}/${chapterNumber}?token=${token}`} data-testid="download-chapter-pdf" className="inline-flex items-center gap-1.5 text-xs text-ff-text-muted hover:text-ff-text transition-colors">
                    <FileText size={14} /> Chapter PDF
                  </a>
                  <a href={`${BACKEND_URL}/api/download/pdf/series/${seriesSlug}?token=${token}`} data-testid="download-series-pdf" className="inline-flex items-center gap-1.5 text-xs text-ff-text-muted hover:text-ff-text transition-colors">
                    <Download size={14} /> Full Series PDF
                  </a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => skip(-15)} data-testid="audio-skip-back" className="w-7 h-7 flex items-center justify-center text-ff-text-muted hover:text-ff-text transition-colors" title="Back 15s">
              <SkipBack size={14} />
            </button>
            <button onClick={toggleAudio} data-testid="audio-play-btn" className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors" style={{ background: `${series.accentColor}30`, color: series.accentColor }}>
              {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <button onClick={() => skip(15)} data-testid="audio-skip-forward" className="w-7 h-7 flex items-center justify-center text-ff-text-muted hover:text-ff-text transition-colors" title="Forward 15s">
              <SkipForward size={14} />
            </button>
            <div className="flex-1 space-y-1">
              <div onClick={handleSeek} className="h-1.5 bg-ff-border rounded-full cursor-pointer relative overflow-hidden" data-testid="audio-progress-bar">
                <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${audioProgress}%`, background: series.accentColor }} />
              </div>
              <div className="flex justify-between text-[10px] text-ff-text-muted">
                <span>{formatTime(audioCurrentTime)}</span>
                <span>{formatTime(audioDuration)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="chapter-page" className="min-h-screen bg-ff-bg">
      {/* Reading Progress Bar */}
      <div className="reading-progress" style={{ width: `${readingProgress}%` }} />

      {/* Hidden audio element */}
      {audioStatus === "ready" && (
        <audio
          ref={audioRef}
          src={`${BACKEND_URL}/api/audio/${seriesSlug}/${chapterNumber}?token=${token}`}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={() => { setIsPlaying(false); savePosition(0); }}
          preload="metadata"
        />
      )}

      {/* Chapter Header */}
      <header className="pt-24 md:pt-32 pb-8 md:pb-12">
        <div className="max-w-reading mx-auto px-4 md:px-6">
          <Link to={`/${seriesSlug}`} data-testid="series-breadcrumb" className="inline-flex items-center gap-2 text-sm text-ff-text-muted hover:text-ff-text transition-colors mb-6">
            <BookOpen size={14} style={{ color: series.accentColor }} /> {series.title}
          </Link>
          <p className="label mb-3" style={{ color: series.accentColor }}>
            {series.slug === "celestial" ? "POST" : "CHAPTER"} {chapterNumber}
          </p>
          <h1 data-testid="chapter-title" className="font-serif text-3xl md:text-4xl text-ff-text">{currentChapter.title}</h1>
          <div className="mt-4">
            <SubstackExportButton seriesSlug={seriesSlug} chapterNumber={chapterNumber} accentColor={series.accentColor} />
          </div>
        </div>
      </header>

      {/* Audio Section — always visible */}
      <AudioSection />

      {/* PDF tools for patrons when audio is not available/playing */}
      {isPatron && audioStatus === "none" && (
        <div data-testid="patron-tools" className="max-w-reading mx-auto px-4 md:px-6 mb-8">
          <div className="flex items-center gap-3 justify-end">
            <a href={`${BACKEND_URL}/api/download/pdf/chapter/${seriesSlug}/${chapterNumber}?token=${token}`} data-testid="download-chapter-pdf" className="inline-flex items-center gap-1.5 text-xs text-ff-text-muted hover:text-ff-text transition-colors">
              <FileText size={14} /> Chapter PDF
            </a>
            <a href={`${BACKEND_URL}/api/download/pdf/series/${seriesSlug}?token=${token}`} data-testid="download-series-pdf" className="inline-flex items-center gap-1.5 text-xs text-ff-text-muted hover:text-ff-text transition-colors">
              <Download size={14} /> Full Series PDF
            </a>
          </div>
        </div>
      )}

      {/* Chapter Content */}
      <article className="pb-16 md:pb-24">
        <div className="max-w-reading mx-auto px-4 md:px-6">
          <ChapterNavigation position="top" />
          <div data-testid="chapter-content" className="py-12">
            {isLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-ff-surface rounded w-3/4" />
                <div className="h-4 bg-ff-surface rounded" />
                <div className="h-4 bg-ff-surface rounded w-5/6" />
                <div className="h-4 bg-ff-surface rounded w-2/3" />
              </div>
            ) : chapterContent?.content ? (
              <div className="prose-reading">
                {chapterContent.content.split("\n\n").map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-ff-text-muted font-light">Chapter content is loading...</p>
              </div>
            )}
          </div>
          <ChapterNavigation position="bottom" />

          {/* Series CTA */}
          <div className="mt-12 p-8 bg-ff-surface border border-ff-border text-center">
            <p className="label text-ff-text-muted mb-3">ENJOYING THE STORY?</p>
            <p className="font-serif text-lg text-ff-text mb-4">Get new chapters delivered weekly.</p>
            <Link to="/subscribe" data-testid="subscribe-cta" className="btn-primary inline-block">Subscribe Free</Link>
          </div>

          {/* Comments Section */}
          <div data-testid="comments-section" className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <MessageCircle size={18} className="text-ff-text-muted" />
              <h2 className="font-serif text-xl text-ff-text">Discussion</h2>
              {comments.length > 0 && <span className="text-ff-text-muted text-sm">({comments.length})</span>}
            </div>

            {isPatron ? (
              <form onSubmit={handlePostComment} data-testid="comment-form" className="mb-8">
                <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Share your thoughts on this chapter..." data-testid="comment-input" className="w-full p-4 bg-ff-surface border border-ff-border text-ff-text placeholder-ff-text-muted resize-none focus:outline-none focus:border-ff-text-muted transition-colors" style={{ fontFamily: "Jost, sans-serif", fontSize: "14px", minHeight: "80px" }} maxLength={2000} />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-ff-text-muted text-xs">{commentText.length}/2000</span>
                  <button type="submit" disabled={!commentText.trim() || commentLoading} data-testid="comment-submit" className="inline-flex items-center gap-1.5 px-5 py-2 text-sm disabled:opacity-30 transition-opacity" style={{ background: series.accentColor, color: "#FFFFFF" }}>
                    <Send size={14} /> {commentLoading ? "Posting..." : "Post"}
                  </button>
                </div>
              </form>
            ) : user ? (
              <div className="mb-8 p-4 bg-ff-surface border border-ff-border text-center">
                <p className="text-ff-text-muted text-sm">
                  <Star size={12} className="inline text-amber-500 mr-1" />
                  Commenting is available to <Link to="/subscribe" className="text-ff-accent hover:underline">Patron subscribers</Link>.
                </p>
              </div>
            ) : (
              <div className="mb-8 p-4 bg-ff-surface border border-ff-border text-center">
                <p className="text-ff-text-muted text-sm">
                  <Link to="/login" state={{ from: `/${seriesSlug}/${chapterSlug}` }} className="text-ff-accent hover:underline">Sign in</Link> to join the discussion.
                </p>
              </div>
            )}

            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((c) => (
                  <div key={c.id} data-testid={`comment-${c.id}`} className="p-4 border-l-2 border-ff-border bg-ff-surface/50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-ff-text text-sm font-medium">{c.user_name}</span>
                        <span className="text-ff-text-muted text-xs">{new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      </div>
                      {user && (user.email === c.user_email || user.is_admin) && (
                        <button onClick={() => handleDeleteComment(c.id)} data-testid={`delete-comment-${c.id}`} className="text-ff-text-muted hover:text-red-400 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <p className="text-ff-text text-sm leading-relaxed" style={{ fontFamily: "Jost, sans-serif" }}>{c.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-ff-text-muted text-sm text-center py-6">No comments yet. Be the first to share your thoughts.</p>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default ChapterPage;
